import { globSync } from "glob";
import type { EntryPoint } from "../types.js";

const MAIN_PATTERNS = [
  // JS/TS
  "src/index.{ts,js,tsx,jsx}",
  "src/main.{ts,js,tsx,jsx}",
  "src/app.{ts,js,tsx,jsx}",
  "index.{ts,js}",
  "server.{ts,js,py}",
  "src/cli.{ts,js}",
  // Go
  "main.go",
  "cmd/*/main.go",
  // Python
  "main.py",
  "app.py",
  "__main__.py",
  "src/__main__.py",
  // Rust
  "src/main.rs",
  "src/lib.rs",
  // Ruby
  "app.rb",
  "lib/*.rb",
  // C# / .NET
  "Program.cs",
  "src/**/Program.cs",
  // Java
  "src/main/java/**/*Application.java",
  "src/main/java/**/Main.java",
];

const TEST_PATTERNS = [
  // JS/TS
  "src/**/*.test.{ts,js,tsx,jsx}",
  "src/**/*.spec.{ts,js,tsx,jsx}",
  // Python
  "tests/**/*.py",
  "test/**/*.py",
  // Go
  "**/*_test.go",
  // C# / .NET
  "**/*.Tests.cs",
  "**/*Tests.cs",
  "**/*Test.cs",
  // Java
  "src/test/java/**/*.java",
  // Rust
  // (inline in src/lib.rs — no separate pattern needed)
];

const CONFIG_PATTERNS = [
  // JS/TS
  "tsconfig.json",
  "vite.config.{ts,js}",
  "next.config.{ts,js,mjs}",
  "webpack.config.{ts,js}",
  "jest.config.{ts,js}",
  "vitest.config.{ts,js}",
  ".eslintrc.{js,json,yml}",
  "eslint.config.{js,mjs}",
  // Python
  "pyproject.toml",
  "setup.py",
  "setup.cfg",
  "tox.ini",
  // Go
  "go.mod",
  // Rust
  "Cargo.toml",
  // C# / .NET / Unity
  "*.sln",
  "*.csproj",
  "src/**/*.csproj",
  "global.json",
  "Directory.Build.props",
  // Java
  "pom.xml",
  "build.gradle",
  "build.gradle.kts",
  "settings.gradle",
  // Ruby
  "Gemfile",
  // Generic
  "Makefile",
  "CMakeLists.txt",
  ".editorconfig",
];

/** Find main entry points, test files, and config files */
export function scanEntryPoints(projectRoot: string): EntryPoint[] {
  const entries: EntryPoint[] = [];
  const seen = new Set<string>();

  const add = (patterns: string[], type: EntryPoint["type"], limit?: number) => {
    let count = 0;
    for (const pattern of patterns) {
      const matches = globSync(pattern, {
        cwd: projectRoot,
        ignore: [
          "node_modules/**", "dist/**", "build/**", ".git/**",
          // .NET / Unity
          "bin/**", "obj/**", "Library/**", "Temp/**", "Logs/**",
          // Python
          ".venv/**", "venv/**", "__pycache__/**",
          // Java
          "target/**", ".gradle/**",
          // Rust
          "target/**",
        ],
      });
      for (const path of matches) {
        if (seen.has(path)) continue;
        seen.add(path);
        entries.push({ path, type });
        count++;
        if (limit && count >= limit) return;
      }
    }
  };

  add(MAIN_PATTERNS, "main");
  add(TEST_PATTERNS, "test", 10); // Cap test files
  add(CONFIG_PATTERNS, "config");

  return entries;
}
